/**
 * Billing-identity editor (T032) — US2. Records 1..N BillingIdentity per client with advisory
 * validation badges + warnings. Persists regardless of resolution (FR-006). Edit requires the
 * finance/admin role (server-enforced, SC-006); other roles see a read-only banner.
 */
import { useState } from 'react';
import type { SessionUser } from '../services/auth';
import type { BillingSystem, IdBasis } from '../types/entities';
import {
  upsertBillingIdentity,
  validateBillingIdentity,
  type ValidationResult,
} from '../services/billing';

const ID_BASIS_BY_SYSTEM: Record<BillingSystem, IdBasis[]> = {
  holded: ['uuid', 'vat'],
  alegra: ['rfc', 'alegra_id'],
};

export function BillingIdentities({ user }: { user: SessionUser }) {
  const canEdit = user.role === 'finance' || user.role === 'admin';

  const [clientId, setClientId] = useState('');
  const [system, setSystem] = useState<BillingSystem>('holded');
  const [externalId, setExternalId] = useState('');
  const [idBasis, setIdBasis] = useState<IdBasis>('uuid');
  const [alegraId, setAlegraId] = useState('');
  const [billingCountry, setBillingCountry] = useState('');

  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSystemChange = (s: BillingSystem) => {
    setSystem(s);
    setIdBasis(ID_BASIS_BY_SYSTEM[s][0]);
    setValidation(null);
  };

  const doValidate = async () => {
    setError(null);
    try {
      setValidation(
        await validateBillingIdentity({
          system,
          external_id: externalId,
          id_basis: idBasis,
          alegra_id: alegraId || null,
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Validation failed');
    }
  };

  const doSave = async () => {
    if (billingCountry && !/^[A-Z]{2}$/.test(billingCountry)) {
      setError('billing_country must be a 2-letter ISO code (e.g. MX, ES).');
      return;
    }
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      const res = await upsertBillingIdentity({
        client_id: clientId,
        system,
        external_id: externalId,
        id_basis: idBasis,
        alegra_id: alegraId || null,
        billing_country: billingCountry,
      });
      setSaved(`${res.doc.billing_identity_id} (resolves=${String(res.doc.resolves)})`);
      setValidation({ resolves: res.doc.resolves, warnings: res.warnings });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <h1>Billing identities</h1>
      {!canEdit && <p style={banner}>Edits require the finance or admin role — read-only here (SC-006).</p>}

      <label style={row}>
        <span style={lbl}>Client (CC-NNN)</span>
        <input value={clientId} onChange={(e) => setClientId(e.target.value.toUpperCase())} placeholder="CC-122" />
      </label>
      <label style={row}>
        <span style={lbl}>System</span>
        <select value={system} onChange={(e) => onSystemChange(e.target.value as BillingSystem)}>
          <option value="holded">holded</option>
          <option value="alegra">alegra</option>
        </select>
      </label>
      <label style={row}>
        <span style={lbl}>external_id (join key)</span>
        <input value={externalId} onChange={(e) => setExternalId(e.target.value)} placeholder="VAT / UUID / RFC" />
      </label>
      <label style={row}>
        <span style={lbl}>id_basis</span>
        <select value={idBasis} onChange={(e) => setIdBasis(e.target.value as IdBasis)}>
          {ID_BASIS_BY_SYSTEM[system].map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </select>
      </label>
      {system === 'alegra' && (
        <label style={row}>
          <span style={lbl}>alegra_id (generic RFC)</span>
          <input value={alegraId} onChange={(e) => setAlegraId(e.target.value)} placeholder="e.g. 5" />
        </label>
      )}
      <label style={row}>
        <span style={lbl}>billing_country</span>
        <input value={billingCountry} onChange={(e) => setBillingCountry(e.target.value.toUpperCase())} maxLength={2} />
      </label>

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button type="button" onClick={() => void doValidate()} disabled={!externalId}>
          Validate
        </button>
        <button
          type="button"
          onClick={() => void doSave()}
          disabled={!canEdit || busy || !clientId || !externalId || !billingCountry}
        >
          {busy ? 'Saving…' : 'Save identity'}
        </button>
      </div>

      {validation && (
        <div style={{ marginTop: 12 }}>
          <span style={validation.resolves ? badgeOk : badgeWarn}>
            {validation.resolves ? '✓ resolves' : '⚠ unresolved (persisted + flagged)'}
          </span>
          {validation.expected_id_basis && (
            <small style={{ marginLeft: 8 }}>expected basis: {validation.expected_id_basis}</small>
          )}
          {validation.warnings.length > 0 && (
            <ul style={{ color: '#a60' }}>
              {validation.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {saved && <p style={{ color: 'green' }}>Saved {saved}</p>}
      {error && <p style={{ color: 'crimson' }}>Error: {error}</p>}
    </section>
  );
}

const row: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', margin: '4px 0' };
const lbl: React.CSSProperties = { width: 180 };
const banner: React.CSSProperties = { background: '#fff7e6', border: '1px solid #ffd591', padding: 8, borderRadius: 4 };
const badgeOk: React.CSSProperties = { background: '#e6ffed', border: '1px solid #56b870', padding: '2px 8px', borderRadius: 4 };
const badgeWarn: React.CSSProperties = { background: '#fff1f0', border: '1px solid #ff7875', padding: '2px 8px', borderRadius: 4 };
